import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Icon } from '../primitives/Icon';
import { Button } from '../primitives/Button';

export interface PermissionDeniedStateProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> {
  /**
   * Optional route to a request-access flow. Note that no other props exist:
   * the component cannot be given an entity name, a record count or a reason,
   * because there is no safe way to render them.
   */
  onRequestAccess?: () => void;
  size?: 'sm' | 'md';
}

/**
 * UX-S07. This component deliberately accepts nothing that could leak: not the
 * name of the thing, not how many rows were withheld, not whether it exists at
 * all. Every unauthorized view in the product renders exactly this text, so the
 * difference between "you may not see it" and "there is nothing to see" is not
 * observable — otherwise the empty state becomes an enumeration oracle.
 */
export const PermissionDeniedState = forwardRef<HTMLDivElement, PermissionDeniedStateProps>(
  function PermissionDeniedState({ onRequestAccess, size = 'md', className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        role="note"
        className={cn(
          'flex flex-col items-center justify-center gap-2 text-center',
          size === 'md' ? 'px-6 py-12' : 'px-4 py-6',
          className,
        )}
        {...rest}
      >
        <span
          aria-hidden="true"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface-sunken text-content-muted"
        >
          <Icon name="lock" size={18} />
        </span>
        <h3 className="text-body-lg font-semibold text-content">Not available to your access</h3>
        <p className="max-w-md text-dense-lg text-content-secondary">
          Your current role and scope do not include this view. If you believe this is wrong, ask
          your administrator to review your access.
        </p>
        {onRequestAccess ? (
          <Button
            variant="secondary"
            size="sm"
            iconStart="shield"
            onClick={onRequestAccess}
            className="mt-2"
          >
            Request access
          </Button>
        ) : null}
      </div>
    );
  },
);
